import { useMemo, useState, useEffect } from "react";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { getUserNameOrEmail } from "@/core/utils/name";
import { useGetReferralSources } from "@/referral-sources/query";
import { ReferralSource } from "@/referral-sources/referral-source";
import { useGetNonAdminUsers } from "@/user/query";
import { DetailedUser } from "@/user/user";

interface SignupSourceOption {
  value: string;
  label: string;
  type: "User" | "MarketingReferralSource" | null;
  sourceId: string;
}

interface SignupSourceSelectorProps {
  id?: string;
  businessId?: string | null;
  selectedSourceId: string;
  selectedSourceType: string;
  onSourceChange: (sourceId: string, sourceType: string) => void;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
}

export const SignupSourceSelector = ({
  id,
  businessId,
  selectedSourceId,
  selectedSourceType,
  onSourceChange,
  disabled = false,
  disabledTooltip,
  label = "Attributed to",
  placeholder = "Select a user or referral source",
  fullWidth = true,
}: SignupSourceSelectorProps) => {
  const {
    data: businessUsers = [],
    isLoading: isLoadingUsers,
    isError: isUsersError,
  } = useGetNonAdminUsers(businessId);
  const {
    data: referralSources = [],
    isLoading: isLoadingSources,
    isError: isSourcesError,
  } = useGetReferralSources(businessId);
  const isError = isUsersError || isSourcesError;
  const [searchValue, setSearchValue] = useState("");

  const signupSourceOptions = useMemo<SignupSourceOption[]>(() => {
    const userOptions: SignupSourceOption[] = businessUsers.map(
      (user: DetailedUser) => ({
        value: user.id,
        label: getUserNameOrEmail(user),
        type: "User",
        sourceId: user.id,
      }),
    );

    const referralSourceOptions: SignupSourceOption[] = referralSources.map(
      (source: ReferralSource) => ({
        value: source.id,
        label: source.display_identification,
        type: "MarketingReferralSource",
        sourceId: source.id,
      }),
    );

    return [
      { value: "none", label: "None", type: null, sourceId: "" },
      ...userOptions,
      ...referralSourceOptions,
    ];
  }, [businessUsers, referralSources]);

  const handleSelectionChange = (value: string) => {
    const selectedOption = signupSourceOptions.find(
      (opt) => opt.value === value,
    );
    const sourceId = value === "none" ? "" : selectedOption?.sourceId || "";
    const sourceType = selectedOption?.type || "";

    onSourceChange(sourceId, sourceType);
  };

  // Find the currently selected option based on both ID and type
  const selectedOption = useMemo(() => {
    if (!selectedSourceId) return null;

    return signupSourceOptions.find(
      (opt) =>
        opt.sourceId === selectedSourceId && opt.type === selectedSourceType,
    );
  }, [selectedSourceId, selectedSourceType, signupSourceOptions]);

  const selectedValue = selectedOption?.value || "none";

  // Sync search value with selected option when it changes
  useEffect(() => {
    if (selectedOption) {
      setSearchValue(selectedOption.label);
    } else {
      setSearchValue("");
    }
  }, [selectedOption]);

  const autocompleteComponent = (
    <AutoComplete
      id={id}
      label={label}
      selectedValue={selectedValue}
      onSelectedValueChange={handleSelectionChange}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      items={
        isError
          ? []
          : signupSourceOptions.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))
      }
      isLoading={isLoadingUsers || isLoadingSources}
      emptyMessage={
        isError
          ? "Couldn’t load users and referral sources. Please try again."
          : "No users or referral sources found"
      }
      placeholder={placeholder}
      fullWidth={fullWidth}
      disabled={disabled}
    />
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger className="w-full" render={<div />}>
          {autocompleteComponent}
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return autocompleteComponent;
};
